import { getDiscountedPrice } from "@/helpers/pricing.js";
import { fanOutAdPixels, postPurchaseContext } from "@/helpers/adPixels.js";

export const GA4_MEASUREMENT_ID = "G-X5DNDBM4XP";
export const ANALYTICS_CURRENCY = "SAR";
const ORDER_CONTEXT_KEY = "tazej_order_analytics";
const PURCHASE_DEDUPE_PREFIX = "tazej_purchase_tracked_";

export function isAnalyticsAvailable() {
    return typeof window !== "undefined";
}

export function pushEvent(eventName, params = {}) {
    if (!isAnalyticsAvailable()) {
        return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...params });
}

/**
 * GA4 recommended ecommerce payload. Clears the previous ecommerce object first.
 * Top-level value/currency/items remain so GTM Data Layer Variables and Ads tags still work.
 */
export function pushEcommerceEvent(eventName, ecommerce = {}, extra = {}, { adPixels = true } = {}) {
    if (!isAnalyticsAvailable()) {
        return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    const payload = {
        event: eventName,
        ecommerce,
        currency: ecommerce.currency,
        value: ecommerce.value,
        items: ecommerce.items,
        ...extra,
    };
    if (ecommerce.transaction_id && payload.transaction_id == null) {
        payload.transaction_id = ecommerce.transaction_id;
    }
    window.dataLayer.push(payload);
    if (adPixels) {
        fanOutAdPixels(eventName, ecommerce, extra);
    }
}

function pushLegacyEvent(legacyName, params = {}) {
    pushEvent(legacyName, { category: "engagement", ...params });
}

function firstPositiveNumber(...candidates) {
    for (const value of candidates) {
        const n = Number(value);
        if (Number.isFinite(n) && n > 0) {
            return n;
        }
    }
    return 0;
}

function firstNonEmptyString(...candidates) {
    for (const value of candidates) {
        if (value == null || value === "") {
            continue;
        }
        if (Array.isArray(value) && value.length >= 2 && value[1]) {
            return String(value[1]).trim() || undefined;
        }
        if (typeof value === "object" && value?.name) {
            const name = String(value.name).trim();
            if (name) {
                return name;
            }
            continue;
        }
        if (typeof value === "string" || typeof value === "number") {
            const text = String(value).trim();
            if (text) {
                return text;
            }
        }
    }
    return undefined;
}

/**
 * Map a catalog product to a GA4 ecommerce item.
 * @param {object} product
 * @param {number} [quantity=1]
 * @param {{ item_category?: string, item_category2?: string, item_list_id?: string, item_list_name?: string, index?: number }} [extras]
 */
export function productToGa4Item(product, quantity = 1, extras = {}) {
    const variant = product?.variants?.[0] ?? product;
    const rawPrice = firstPositiveNumber(
        variant?.minimum_price,
        product?.minimum_price,
        variant?.list_price,
        product?.list_price,
        product?.price
    );
    const discountPercent =
        typeof variant?.discount_percent === "number"
            ? variant.discount_percent
            : typeof product?.discount_percent === "number"
              ? product.discount_percent
              : 0;
    const price = parseFloat(getDiscountedPrice(rawPrice, discountPercent)) || 0;

    const item_category = firstNonEmptyString(
        extras.item_category,
        product?.item_category,
        product?.category_name,
        product?.categoryName,
        product?.category,
        product?.categ_name,
        product?.categ_id,
        product?.public_categ_id
    );
    const item_category2 = firstNonEmptyString(
        extras.item_category2,
        product?.item_category2,
        product?.category_name2,
        product?.subcategory,
        product?.sub_category,
        product?.public_categ_ids?.[0]
    );

    const item = {
        item_id: String(variant?.id ?? product?.id ?? ""),
        item_name: String(variant?.name ?? product?.name ?? ""),
        quantity: Math.max(1, Number(quantity) || 1),
        price,
    };

    if (item_category) {
        item.item_category = item_category;
    }
    if (item_category2) {
        item.item_category2 = item_category2;
    }
    if (extras.item_list_id != null && extras.item_list_id !== "") {
        item.item_list_id = String(extras.item_list_id);
    }
    if (extras.item_list_name != null && extras.item_list_name !== "") {
        item.item_list_name = String(extras.item_list_name);
    }
    if (extras.index != null && Number.isFinite(Number(extras.index))) {
        item.index = Number(extras.index);
    }

    return item;
}

export function buildItemsFromCartProducts(products, countsByKey = null) {
    if (!Array.isArray(products)) {
        return [];
    }
    return products.map((product) => {
        const variantId = product?.variants?.[0]?.id ?? product?.id;
        const key = variantId ?? product?.id;
        const qty =
            countsByKey && key != null && countsByKey[key] != null
                ? countsByKey[key]
                : 1;
        return productToGa4Item(product, qty);
    });
}

export function sumItemsValue(items) {
    return items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);
}

/** E.164 for Google Ads enhanced conversions / offline import (e.g. +966534700646). */
export function formatPhoneE164(phone) {
    if (phone == null || phone === "") {
        return undefined;
    }
    const digits = String(phone).replace(/\D/g, "");
    if (!digits) {
        return undefined;
    }
    let normalized = digits;
    if (normalized.startsWith("00966")) {
        normalized = "966" + normalized.slice(5);
    } else if (normalized.startsWith("0")) {
        normalized = "966" + normalized.slice(1);
    } else if (normalized.startsWith("5") && normalized.length === 9) {
        normalized = "966" + normalized;
    } else if (!normalized.startsWith("966")) {
        return undefined;
    }
    return `+${normalized}`;
}

/** Lowercase email for Google user-provided data (hashed by Google tag if unhashed). */
export function normalizeEmailForAds(email) {
    if (!email || typeof email !== "string") {
        return undefined;
    }
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
        return undefined;
    }
    return trimmed;
}

export function buildUserProvidedData({ email, phone } = {}) {
    const normalizedEmail = normalizeEmailForAds(email);
    const phone_number = formatPhoneE164(phone);
    if (!normalizedEmail && !phone_number) {
        return undefined;
    }
    const data = {};
    if (normalizedEmail) {
        data.email = normalizedEmail;
    }
    if (phone_number) {
        data.phone_number = phone_number;
    }
    return data;
}

function readStoredUserContact() {
    if (!isAnalyticsAvailable()) {
        return null;
    }
    try {
        const raw = localStorage.getItem("userData");
        const parsed = raw ? JSON.parse(raw) : null;
        if (!parsed || typeof parsed !== "object") {
            return null;
        }
        let city;
        try {
            const area = JSON.parse(localStorage.getItem("selectedAria") || "null");
            city = area?.name || undefined;
        } catch {
            city = undefined;
        }
        return {
            email: parsed.email,
            phone: parsed.phone,
            name: parsed.name,
            external_id: parsed.id,
            city,
        };
    } catch {
        return null;
    }
}

export function trackPageView(route) {
    if (!isAnalyticsAvailable() || !route) {
        return;
    }
    pushEvent("page_view", {
        page_path: route.fullPath,
        page_location: window.location.href,
        page_title: typeof document !== "undefined" ? document.title : route.meta?.title || "",
    });
    fanOutAdPixels("page_view", {}, { page_path: route.fullPath });
}

export function trackViewItem(
    product,
    { isSeasonal = false, isFastDelivery = false, itemListId, itemListName, categoryName } = {}
) {
    const resolvedListId =
        itemListId != null && itemListId !== ""
            ? String(itemListId)
            : isFastDelivery
              ? "fast_delivery"
              : isSeasonal
                ? "seasonal"
                : "catalog";
    const resolvedListName =
        itemListName != null && itemListName !== ""
            ? String(itemListName)
            : categoryName || resolvedListId;
    const items = [
        productToGa4Item(product, 1, {
            item_category: categoryName,
            item_list_id: resolvedListId,
            item_list_name: resolvedListName,
        }),
    ];
    const value = sumItemsValue(items);
    pushEcommerceEvent(
        "view_item",
        {
            currency: ANALYTICS_CURRENCY,
            value,
            items,
            item_list_id: resolvedListId,
            item_list_name: resolvedListName,
        },
        { item_list_id: resolvedListId, item_list_name: resolvedListName }
    );
    pushLegacyEvent("web_open_product", {
        product_id: product?.id,
        product_name: product?.name,
    });
}

/**
 * GA4 view_item_list — product grids (home categories, search, fast delivery).
 */
export function trackViewItemList({
    products = [],
    itemListId,
    itemListName,
    categoryName,
} = {}) {
    const listId = String(itemListId ?? categoryName ?? "catalog");
    const listName = String(itemListName ?? categoryName ?? listId);
    const items = (Array.isArray(products) ? products : []).map((product, index) =>
        productToGa4Item(product, 1, {
            item_category: categoryName,
            item_list_id: listId,
            item_list_name: listName,
            index,
        })
    );
    if (!items.length) {
        return;
    }
    pushEcommerceEvent(
        "view_item_list",
        {
            currency: ANALYTICS_CURRENCY,
            value: sumItemsValue(items),
            items,
            item_list_id: listId,
            item_list_name: listName,
        },
        { item_list_id: listId, item_list_name: listName }
    );
}

/**
 * GA4 select_item — product click from a list.
 */
export function trackSelectItem(
    product,
    { itemListId, itemListName, categoryName, index } = {}
) {
    const listId = String(itemListId ?? categoryName ?? "catalog");
    const listName = String(itemListName ?? categoryName ?? listId);
    const items = [
        productToGa4Item(product, 1, {
            item_category: categoryName,
            item_list_id: listId,
            item_list_name: listName,
            index,
        }),
    ];
    pushEcommerceEvent(
        "select_item",
        {
            currency: ANALYTICS_CURRENCY,
            value: sumItemsValue(items),
            items,
            item_list_id: listId,
            item_list_name: listName,
        },
        { item_list_id: listId, item_list_name: listName }
    );
}

/** Custom engagement: category nav click (not scroll-spy). */
export function trackSelectCategory({ categoryId, categoryName } = {}) {
    if (categoryId == null && !categoryName) {
        return;
    }
    pushEvent("select_category", {
        category_id: categoryId != null ? String(categoryId) : undefined,
        category_name: categoryName || undefined,
    });
}

/** GA4 recommended search event. */
export function trackSearch(searchTerm) {
    const term = searchTerm != null ? String(searchTerm).trim() : "";
    if (!term) {
        return;
    }
    pushEvent("search", { search_term: term });
}

export function trackAddToCart(product, { cartType = "regular", quantity = 1 } = {}) {
    const items = [productToGa4Item(product, quantity)];
    const value = sumItemsValue(items);
    pushEcommerceEvent(
        "add_to_cart",
        {
            currency: ANALYTICS_CURRENCY,
            value,
            items,
        },
        { cart_type: cartType }
    );

    const legacyMap = {
        regular: "web_add_to_regular_cart",
        seasonal: "web_add_to_Seasonal_cart",
        fast: "web_add_to_fast_cart",
        fast_delivery: "web_add_to_fast_delivery_cart",
    };
    const legacy = legacyMap[cartType] || legacyMap.regular;
    pushLegacyEvent(legacy, {
        product_id: product?.id,
        product_name: product?.name,
    });
}

export function trackViewCart({ value, items = [], cartType = "regular" } = {}) {
    const resolvedValue = Number(value) || sumItemsValue(items);
    pushEcommerceEvent(
        "view_cart",
        {
            currency: ANALYTICS_CURRENCY,
            value: resolvedValue,
            items,
        },
        { cart_type: cartType }
    );
}

export function trackRemoveFromCart(product, { quantity = 1, cartType = "regular" } = {}) {
    const items = [productToGa4Item(product, quantity)];
    pushEcommerceEvent(
        "remove_from_cart",
        {
            currency: ANALYTICS_CURRENCY,
            value: sumItemsValue(items),
            items,
        },
        { cart_type: cartType }
    );
}

export function trackBeginCheckout({ value, items, cartType = "regular" }) {
    const resolvedItems = items || [];
    const resolvedValue = Number(value) || sumItemsValue(resolvedItems);
    pushEcommerceEvent(
        "begin_checkout",
        {
            currency: ANALYTICS_CURRENCY,
            value: resolvedValue,
            items: resolvedItems,
        },
        { cart_type: cartType }
    );
    const legacy =
        cartType === "fast" ? "web_open_fast_cart_confirmation" : "web_open_regular_cart_confirmation";
    pushLegacyEvent(legacy, { url: window.location.href });
}

export function trackAddShippingInfo({ value, items, shippingTier, cartType = "regular" } = {}) {
    const resolvedItems = items || [];
    pushEcommerceEvent(
        "add_shipping_info",
        {
            currency: ANALYTICS_CURRENCY,
            value: Number(value) || sumItemsValue(resolvedItems),
            items: resolvedItems,
            shipping_tier: shippingTier,
        },
        { shipping_tier: shippingTier, cart_type: cartType }
    );
}

export function trackAddPaymentInfo({ value, items, paymentType, paymentMethod }) {
    const resolvedItems = items || [];
    pushEcommerceEvent(
        "add_payment_info",
        {
            currency: ANALYTICS_CURRENCY,
            value: Number(value) || sumItemsValue(resolvedItems),
            items: resolvedItems,
            payment_type: paymentType,
        },
        {
            payment_type: paymentType,
            payment_method: paymentMethod || paymentType,
        }
    );
}

export function trackAddToWishlist(product, { quantity = 1 } = {}) {
    const items = [productToGa4Item(product, quantity)];
    pushEcommerceEvent("add_to_wishlist", {
        currency: ANALYTICS_CURRENCY,
        value: sumItemsValue(items),
        items,
    });
}

export function saveOrderAnalyticsContext({ orderId, value, items, email, phone, name, city, country, externalId }) {
    if (!isAnalyticsAvailable() || orderId == null) {
        return;
    }
    const contact = readStoredUserContact();
    postPurchaseContext({
        orderId,
        items: items || [],
        externalId: externalId ?? contact?.external_id,
    });
    try {
        localStorage.setItem(
            ORDER_CONTEXT_KEY,
            JSON.stringify({
                orderId: String(orderId),
                value: Number(value) || sumItemsValue(items),
                currency: ANALYTICS_CURRENCY,
                items: items || [],
                email: normalizeEmailForAds(email) ?? contact?.email,
                phone: phone ?? contact?.phone,
                name: name ?? contact?.name,
                city: city ?? contact?.city,
                country: country || "sa",
                external_id: externalId ?? contact?.external_id,
            })
        );
    } catch {
        /* ignore quota errors */
    }
}

export function readOrderAnalyticsContext() {
    if (!isAnalyticsAvailable()) {
        return null;
    }
    try {
        const raw = localStorage.getItem(ORDER_CONTEXT_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

/** localStorage so a new tab or a later visit to the confirmation page cannot re-send the purchase. */
function wasPurchaseTracked(key) {
    try {
        if (localStorage.getItem(key)) {
            return true;
        }
    } catch {
        /* storage unavailable */
    }
    try {
        return Boolean(sessionStorage.getItem(key));
    } catch {
        return false;
    }
}

function markPurchaseTracked(key) {
    try {
        localStorage.setItem(key, "1");
    } catch {
        /* storage unavailable */
    }
    try {
        sessionStorage.setItem(key, "1");
    } catch {
        /* storage unavailable */
    }
}

export function trackPurchase({
    transactionId,
    value,
    items,
    paymentType,
    email,
    phone,
    name,
    city,
    country,
    externalId,
    adPixels = true,
}) {
    if (!isAnalyticsAvailable() || transactionId == null) {
        return;
    }
    const dedupeKey = PURCHASE_DEDUPE_PREFIX + String(transactionId);
    if (wasPurchaseTracked(dedupeKey)) {
        return;
    }
    markPurchaseTracked(dedupeKey);

    const resolvedValue = Number(value) || sumItemsValue(items);
    const currency = ANALYTICS_CURRENCY;
    const conversionTime = new Date().toISOString();
    const contact = readStoredUserContact();
    const userProvided = buildUserProvidedData({
        email: email ?? contact?.email,
        phone: phone ?? contact?.phone,
    });

    const payload = {
        currency,
        transaction_id: String(transactionId),
        value: resolvedValue,
        items: items || [],
        payment_type: paymentType || undefined,
        conversion_value: resolvedValue,
        conversion_currency: currency,
        conversion_time: conversionTime,
        name: name ?? contact?.name,
        city: city ?? contact?.city,
        country: country || "sa",
        external_id: externalId ?? contact?.external_id,
    };

    if (userProvided) {
        payload.user_data = userProvided;
        payload.leadsUserData = userProvided;
        if (userProvided.email) {
            payload.email = userProvided.email;
        }
        if (userProvided.phone_number) {
            payload.phone_number = userProvided.phone_number;
            // Meta CAPI fan-out reads `phone` as well as `phone_number`
            payload.phone = userProvided.phone_number;
        }
    }

    const { currency: payloadCurrency, transaction_id, value: payloadValue, items: payloadItems, ...extra } =
        payload;
    pushEcommerceEvent(
        "purchase",
        {
            currency: payloadCurrency,
            transaction_id,
            value: payloadValue,
            items: payloadItems,
        },
        extra,
        { adPixels }
    );
}

/**
 * Cash orders are created as drafts and may be cancelled; their ad-platform Purchase is sent
 * server-side only once Odoo confirms them. GA4 still gets the browser purchase.
 */
export function trackPurchaseFromStoredContext(paymentType) {
    const ctx = readOrderAnalyticsContext();
    if (!ctx?.orderId) {
        return;
    }
    trackPurchase({
        transactionId: ctx.orderId,
        value: ctx.value,
        items: ctx.items,
        paymentType,
        email: ctx.email,
        phone: ctx.phone,
        name: ctx.name,
        city: ctx.city,
        country: ctx.country,
        externalId: ctx.external_id,
        adPixels: paymentType !== "cash",
    });
}

export function trackFirstOpen() {
    pushLegacyEvent("web_first_open", {
        method: "user_open_vue_app",
        url: window.location.href,
    });
}

export function trackLogin(method = "phone_otp") {
    pushEvent("login", { method });
    fanOutAdPixels("login", {}, { method });
}

export function trackSignUp(method = "phone_otp") {
    pushEvent("sign_up", { method });
    fanOutAdPixels("sign_up", {}, { method });
}

export function trackFastDeliveryPageOpen() {
    pushLegacyEvent("web_open_fast_delivery_page", { url: window.location.href });
}
